import { Paper, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { multiplyBorderRadius } from '@shared/theme/util';
import { forwardRef, useState } from 'react';
import { useChatbot } from '@/feature/chatbot/useChatbot';
import { useTexts } from '@/feature/lang';
import Translate from '../Translate';
import { ChatInput } from './ChatInput';
import { ChatWindowHeader } from './ChatWindowHeader';
import { NoContent } from './components/NoContent';
import { History } from './History';
import { Messages } from './Messages';
import {
  CHAT_HEIGHT,
  CHAT_WIDTH,
  chatbotStyles,
  HISTORY_WIDTH,
  MIN_CHAT_HEIGHT,
} from './styles';

const ChatContainer = styled(Paper, {
  shouldForwardProp: (prop) =>
    ![
      'isMobile',
      'isFullscreen',
      'empty',
      'customWidth',
      'customHeight',
    ].includes(prop as string),
})<{
  isMobile: boolean;
  isFullscreen: boolean;
  empty: boolean;
  customWidth?: number;
  customHeight?: number;
}>(({ theme, isMobile, isFullscreen, empty, customWidth, customHeight }) => ({
  position: 'fixed',
  ...(isMobile || isFullscreen
    ? {
        right: 0,
        bottom: 0,
        height: '100vh',
        width: '100vw',
        border: 'none',
        backgroundColor: theme.palette.background.paper,
      }
    : {
        bottom: theme.spacing(3),
        right: theme.spacing(3),
        height:
          empty && !customHeight
            ? MIN_CHAT_HEIGHT
            : (customHeight ?? CHAT_HEIGHT),
        width: customWidth ?? CHAT_WIDTH,
        maxWidth: `calc(100vw - ${theme.spacing(6)})`,
        maxHeight: `calc(100vh - ${theme.spacing(6)})`,
        ...chatbotStyles.paper(theme),
      }),
  display: 'flex',
  flexDirection: 'column',
  borderRadius: isMobile || isFullscreen ? 0 : multiplyBorderRadius(theme, 3),
  zIndex: theme.zIndex.modal,
  boxShadow: theme.shadows[8],
  overflow: 'hidden',
}));

const ContentContainer = styled('div')({
  display: 'flex',
  flexGrow: 1,
  overflow: 'hidden',
  minWidth: 0,
});

const ChatContent = styled('div')({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minWidth: 0,
  height: '100%',
  width: '100%',
  '& > *': {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
});

const MessagesContainer = styled('div')({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
});

const SidePanel = styled('div', {
  shouldForwardProp: (prop) => prop !== 'width',
})<{ width: number }>(({ theme, width }) => ({
  height: '100%',
  width: `${width}px`,
  flexShrink: 0,
  overflow: 'hidden',
  borderRight: `1px solid ${theme.palette.divider}`,
}));

const EmptyWrapper = styled('div')({
  flexGrow: 1,
  display: 'flex',
  overflow: 'hidden',
  justifyContent: 'center',
});

const Disclaimer = styled('div')(({ theme }) => ({
  padding: theme.spacing(0.25, 2),
  fontSize: '10px',
  color: theme.palette.text.disabled,
  flexShrink: 0,
  zIndex: 1,
}));

const BottomSection = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
});

interface ChatWindowProps {
  onClose: () => void;
  customWidth?: number;
  customHeight?: number;
  onResize: (width: number, height: number) => void;
}

export const ChatWindow = forwardRef<HTMLDivElement, ChatWindowProps>(
  function ChatWindow({ onClose, customWidth, customHeight, onResize }, ref) {
    const texts = useTexts().component.chatbot;
    const { chatContext, messages, isLoading, handleUserInput, isChatEmpty } =
      useChatbot();
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleToggleFullscreen = () => {
      setIsFullscreen(!isFullscreen);
    };

    const handleToggleHistory = () => {
      setIsHistoryOpen(!isHistoryOpen);
      onResize(
        (customWidth ?? CHAT_WIDTH) + HISTORY_WIDTH * (!isHistoryOpen ? 1 : -1),
        customHeight ?? CHAT_HEIGHT
      );
    };

    return (
      <ChatContainer
        ref={ref}
        isMobile={isMobile}
        isFullscreen={isFullscreen}
        empty={isChatEmpty && !isHistoryOpen}
        customWidth={customWidth}
        customHeight={customHeight}
        elevation={0}
      >
        <ChatWindowHeader
          onClose={onClose}
          onToggleFullscreen={handleToggleFullscreen}
          onToggleHistory={handleToggleHistory}
          isFullscreen={isFullscreen}
          isHistoryOpen={isHistoryOpen}
          onResize={onResize}
          chatWindowRef={ref}
        />
        <ContentContainer>
          {isHistoryOpen && (
            <SidePanel width={HISTORY_WIDTH}>
              <History onClose={handleToggleHistory} />
            </SidePanel>
          )}
          <ChatContent>
            <MessagesContainer>
              {chatContext.isValid && (!isChatEmpty || isLoading) ? (
                <Messages messages={messages} isLoading={isLoading} />
              ) : (
                <EmptyWrapper>
                  <NoContent />
                </EmptyWrapper>
              )}
            </MessagesContainer>
            <BottomSection>
              {!isChatEmpty && (
                <Disclaimer>
                  <Translate text={texts.disclaimer} />
                </Disclaimer>
              )}
              <ChatInput
                onSendMessage={handleUserInput}
                disabled={!chatContext.isValid}
              />
            </BottomSection>
          </ChatContent>
        </ContentContainer>
      </ChatContainer>
    );
  }
);
